/* eslint-disable @angular-eslint/prefer-inject */
import { EquipmentDataService } from '../infrastructure/equipments/equipment.data.service';
import {
    EquipmentCreate,
    EquipmentDto,
    EquipmentRead,
    EquipmentSearchParams,
} from '../entities/equipment.dto';
import { EquipmentStore } from '../infrastructure/equipments/equipment.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { EquipmentMapper } from '../infrastructure/equipments/equipment.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { IBarcodeSearchFacade, IQueryParams, SafeAny } from '@pf/shared-common';
import { ContainerFacade } from './container.facade';
import { map, Observable, tap } from 'rxjs';
import { asArraySafe } from '@pf/shared-utility';
import { AutoIncrementerFacade } from '@pf/platform-core/domain';
import { WarehouseFacade } from './warehouse.facade';
import { ResourceAuth } from '@control-tower/platform-wms';

@Injectable()
export class EquipmentFacade
    extends AbstractManageEntityFacade<
        EquipmentDto,
        EquipmentRead,
        EquipmentCreate,
        EquipmentSearchParams
    >
    implements IBarcodeSearchFacade
{
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly containerFacade = inject(ContainerFacade);
    private readonly autoIncrementerFacade = inject(AutoIncrementerFacade);
    nameField: keyof EquipmentDto = 'name';

    constructor(
        dataService: EquipmentDataService,
        store: EquipmentStore,
        mapper: EquipmentMapper
    ) {
        super(dataService, null, store, mapper);
    }

    override getDefaultSearchParams$(
        params: IQueryParams<EquipmentDto>
    ): Observable<IQueryParams<EquipmentDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: EquipmentCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    searchByBarcode$(barcodeText: string): Observable<EquipmentDto> {
        return this.oneTimeSearch$({
            pageIndex: 1,
            pageSize: 1,
            filters: {
                barcode: barcodeText,
            },
        }).pipe(map(result => result.data?.[0]));
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof EquipmentDto, string>> {
        return { name: searchText };
    }

    unassignContainer$(equipment: EquipmentDto, containerId: string) {
        return this.containerFacade.removeEquipment$(containerId).pipe(
            tap(() => {
                equipment.containers = asArraySafe(equipment.containers).filter(
                    c => c.id !== containerId
                );
                this.store.update(this.mapper.toEntity(equipment));
            }),
            map(() => equipment)
        );
    }

    assignContainer$(equipment: EquipmentDto, containerId: string) {
        return this.containerFacade
            .setEquipment$(containerId, equipment.id)
            .pipe(
                tap(container => {
                    equipment.containers = [
                        ...asArraySafe(equipment.containers),
                        container,
                    ];
                    this.store.update(this.mapper.toEntity(equipment));
                }),
                map(() => equipment)
            );
    }

    generateBarcode(equipment: EquipmentDto) {
        const type = `EquipmentType_${equipment.equipmentType.code}_${equipment.warehouse.id}`;
        return this.autoIncrementerFacade.getIncrement(type);
    }
}
